import * as SHARED from '@shefira/shared';

/**
 * Every French text of the game data a player reads (ADR 0069, step 3):
 * item, creature, spell and quest names and lines, achievements, offers,
 * regions and places. One walk over what `@shefira/shared` exports, used
 * three ways: to put the English in place at start (`localize-data.ts`),
 * to list what needs translating, and by the test that nothing lacks it.
 *
 * A text is found by its shape, not by a list of fields: a string with a
 * letter and a space, an accent or a capitalised word, that is not an id,
 * an asset key, a colour or an address. Fields that only ever hold ids are
 * skipped outright, so a mob id is never mistaken for a word.
 */

/** Exports that are not player-facing text, or are read elsewhere (the changelog). */
const SKIPPED_EXPORTS =
  /^(GENERATED_WORLD|WORLD_EDITS|NO_WORLD_EDITS|CLIFF_WINDOWS|CHANGELOG|BANNED_WORDS|ZONES|CONTACT_EMAIL|.*Schema|.*_SCHEMA.*)$/;

/** Fields that hold ids, keys or asset names, never words. */
const ID_FIELDS = new Set([
  'id',
  'icon',
  'sprite',
  'bodyKey',
  'key',
  'mob',
  'biome',
  'zone',
  'slot',
  'kind',
  'rarity',
  'classId',
  'templateId',
  'item',
  'items',
  'fx',
  'prop',
  'ground',
  'music',
  'to',
  'portal',
  'landmark',
  'requires',
  'mobs',
  'sound',
  'tint',
  'effect',
  'target',
  'targeting',
  'lairs',
  'services',
  'buildings',
  'houses',
  'dens',
  'elites',
  'stable',
  // A place referred to by its name: it must match the landmark's French name.
  'lair',
  'near',
  'village',
]);

export function isDataText(value: string): boolean {
  return (
    value.length > 1 &&
    /\p{L}/u.test(value) &&
    (/[ ’'éèàùêâîôûçœ…«»,.!?:]/.test(value) || /^[A-ZÉÀÂ][a-zéèàùêâîôûç-]+$/.test(value)) &&
    !/^[a-z0-9]+([-_.][a-z0-9]+)+$/.test(value) &&
    !/^(icon|overlay|prop|sfx|music|tiles|mob|body|effects?)\./.test(value) &&
    !/^#[0-9a-f]{3,8}$/i.test(value) &&
    !/^https?:/.test(value)
  );
}

/** Where a text sits, so it can be read and replaced in place. */
export interface DataText {
  /** A readable path, `QUESTS[meadow-1].offer`, for lists and test messages. */
  path: string;
  text: string;
  /** Put another text in its place; false when its holder is frozen. */
  replace: (next: string) => boolean;
  /**
   * False for a name the code compares (a zone's, a place's: quests, lairs
   * and the Company's quarter find places by it). Those stay French in the
   * data and are translated where they are shown, by `uiText`.
   */
  inPlace: boolean;
}

const MAX_DEPTH = 8;

export function forEachDataText(visit: (found: DataText) => void): void {
  const seen = new Set<unknown>();
  const walk = (
    value: unknown,
    path: string,
    depth: number,
    put: (next: string) => boolean,
  ): void => {
    if (depth > MAX_DEPTH || value === null || value === undefined) return;
    if (typeof value === 'string') {
      if (isDataText(value)) visit({ path, text: value, replace: put, inPlace: true });
      return;
    }
    if (typeof value !== 'object' || seen.has(value)) return;
    seen.add(value);
    if (value instanceof Map) {
      for (const [key, item] of value as Map<unknown, unknown>) {
        walk(item, `${path}[${String(key)}]`, depth + 1, (next) => {
          (value as Map<unknown, unknown>).set(key, next);
          return true;
        });
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item: unknown, index) => {
        const label = isRecord(item) && typeof item.id === 'string' ? item.id : String(index);
        walk(item, `${path}[${label}]`, depth + 1, (next) => assign(value, index, next));
      });
      return;
    }
    for (const [key, item] of Object.entries(value)) {
      if (ID_FIELDS.has(key) && typeof item === 'string') continue;
      walk(item, `${path}.${key}`, depth + 1, (next) => assign(value, key, next));
    }
  };
  for (const [name, value] of Object.entries(SHARED)) {
    if (SKIPPED_EXPORTS.test(name) || typeof value === 'function') continue;
    walk(value, name, 0, () => false);
  }
  // Zones: their names and their places' names only, never the map itself.
  for (const [id, zone] of SHARED.ZONES) {
    if (isDataText(zone.name)) {
      visit({
        path: `ZONES[${id}].name`,
        text: zone.name,
        replace: (next) => assign(zone, 'name', next),
        inPlace: false,
      });
    }
    for (const landmark of zone.landmarks) {
      if (!isDataText(landmark.name)) continue;
      visit({
        path: `ZONES[${id}].landmarks[${landmark.name}]`,
        text: landmark.name,
        replace: (next) => assign(landmark, 'name', next),
        inPlace: false,
      });
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function assign(holder: object, key: string | number, next: string): boolean {
  if (Object.isFrozen(holder)) return false;
  (holder as Record<string | number, unknown>)[key] = next;
  return true;
}
